import {Component, Inject, OnInit, QueryList, ViewChildren} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {PathwayConstants} from "@tva/ng-migration/cre-tracking/components/search/cre-management-search-results/pathway/pathway.constants";
import {PathwaySchoolCourse} from "@tva/ng-migration/cre-tracking/models";
import {first} from "rxjs/operators";
import {PathwayRequirementsConstants} from "./pathway-requirements.constants";
import {
  CourseNameValidator,
  DuplicatedCourseValidator,
  DuplicatedNonK12CourseValidator
} from "./pathway-requirement.validators";
import {UnsavedChangesService} from "@tva/ng-migration/shared/services/unsavedChanges.service";
import {NotificationService, TvActionChipsComponent} from "@tva/ng-migration/uicomponents";

@Component({
    selector: 'app-pathway-requirements-modal',
    templateUrl: './pathway-requirements-modal.component.html',
    styleUrls: ['./pathway-requirements-modal.component.scss'],
})
export class PathwayRequirementsModalComponent implements OnInit {
    displayColumns = ["name", "actions"];
    pathwaysCoursesDataSource: MatTableDataSource<any>;
    pathwayCourses: FormArray;
    wasModified: boolean;
    wasValidated: boolean;
    schoolCourses: [any];
    pathwayCoursesOriginal: [any];

    @ViewChildren(TvActionChipsComponent) courseLists: QueryList<TvActionChipsComponent>;


    constructor(@Inject(MAT_DIALOG_DATA) public data: { schoolCourses: any, pathwayCourses: any},
                public dialogRef: MatDialogRef<PathwayRequirementsModalComponent>,
                private unsavedChanges: UnsavedChangesService,
                private notificationService: NotificationService) {
        this.schoolCourses = data.schoolCourses;
        this.pathwayCoursesOriginal = data.pathwayCourses;
    }

    get errorMessages(){
        return PathwayRequirementsConstants.errors;
    }

    ngOnInit(): void {
        this.dialogRef.disableClose = true;
        this.pathwayCourses = new FormArray([], [Validators.required, DuplicatedCourseValidator, DuplicatedNonK12CourseValidator]);
        this.pathwaysCoursesDataSource = new MatTableDataSource(this.pathwayCourses.controls);
        this.loadCoursesEntries();
    }

    addNewCourse(){
      const form = this.createForm();
      this.pathwayCourses.push(form);
      this.notifyModification();
      this.reloadTable();
    }

    deleteCourse(course: any){
        const i = this.pathwayCourses.controls.indexOf(course);
        this.pathwayCourses.removeAt(i);
        this.notifyModification();
        this.reloadTable();
    }

    hasNonK12CourseNameField(pathwayCourse: FormGroup): boolean {
        const course = pathwayCourse.get('course').value;
        return course.length && course[0] == PathwayConstants.non_k12_course;

    }

    loadCoursesEntries(){
        this.pathwayCoursesOriginal.forEach((pathwayCourse) => {
            const form = this.createForm();
            const courses: PathwaySchoolCourse[] = [];
            if (pathwayCourse.courseId) {
                courses.push({courseId: pathwayCourse.courseId, courseName: pathwayCourse.name});
            } else {
                courses.push(PathwayConstants.non_k12_course);
                form.get('name').setValue(pathwayCourse.name);
            }
            form.get('course').setValue(courses);
            this.loadControlTriggerChange(form);
            this.pathwayCourses.push(form);
        });
        this.reloadTable();
    }

    errorIsShown(control: AbstractControl): boolean{
        return this.wasValidated && control.touched;
    }

    done() {
        if (this.validate()){
            this.dialogRef.close(this.pathwayCourses.value);
            this.unsavedChanges.startChanges();
            this.notificationService.hideErrorMessage();
        } else {
            this.notificationService.showErrorMessage();
        }
    }

    close() {
        if (this.wasModified){
            this.unsavedChanges.ask(()=> {
                this.dialogRef.close();
            }, PathwayRequirementsConstants.unsaved_messages);
        } else {
            this.dialogRef.close();
        }
    }

  private reloadTable(){
    this.pathwaysCoursesDataSource.data = this.pathwayCourses.controls;
  }

  private loadControlTriggerChange(control: AbstractControl) {
    control.valueChanges.pipe(first()).subscribe(() => {
      this.notifyModification();
    })
  }

  private notifyModification() {
    this.wasModified = true;
  }

  private createForm(): FormGroup {
    return new FormGroup({
      course: new FormControl([], Validators.required),
      name: new FormControl()
    },[CourseNameValidator]);
  }

  private validate(): boolean{
    this.courseLists.forEach(courseList => courseList.validate());
    const invalidForms = this.pathwayCourses.controls.filter(pathwayCourseForm => {
      pathwayCourseForm.markAsTouched();
      return pathwayCourseForm.invalid;
    });
    this.pathwayCourses.markAsTouched();
    this.wasValidated = true;
    return this.pathwayCourses.valid && !invalidForms.length;
  }
}
